import React from "react";
import { Building2, ShieldCheck, Workflow } from "lucide-react";

/* ─────────────────────────────────────────────────────────────
   MarketingAgencyFit · Phase 4 Homepage
   ------------------------------------------------------------
   Medium-weight light section. 40/60 composition.

   All statements verified against the codebase:
     • "Designed for UK transactions" — the whole product model
       (TA6, memorandum of sale, freehold/leasehold, exchange,
       en-GB currency/date formatting) is UK-specific.
     • "Role-based access" — backend `auth.py` exposes
       `require_role(*allowed_roles)` and every JWT carries a
       role claim (agent / admin / …) used by protected routes.
     • "One dedicated space for progression" — factual product
       positioning; no CRM-replacement or integration claim.

   NOT claimed (unverified):
     • "Works with your existing CRM"
     • GDPR / ISO / SOC 2 / "bank-grade security"
     • encrypted at rest
     • customer numbers / adoption metrics
   ───────────────────────────────────────────────────────────── */

const ROWS = [
  {
    Icon: Building2,
    label: "Designed for UK transactions",
    body: "Workflows built around UK residential sales progression — memorandum of sale, TA6, searches, enquiries, exchange.",
  },
  {
    Icon: ShieldCheck,
    label: "Role-based access",
    body: "Every user carries a role. Teams see the transactions and actions that belong to them.",
  },
  {
    Icon: Workflow,
    label: "One dedicated space for progression",
    body: "A workspace for progressing sales — the Deal Room, activity, documents and communication all in one place.",
  },
];

export const MarketingAgencyFit = () => (
  <section
    id="for-agencies-fit"
    className="relative"
    style={{ background: "#F8FAFC" }}
    data-testid="landing-agency-fit"
  >
    <div
      aria-hidden="true"
      className="absolute inset-x-0 top-0 h-px"
      style={{ background: "linear-gradient(90deg, transparent 0%, rgba(15,23,42,0.08) 50%, transparent 100%)" }}
    />
    <div className="max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-10 pt-16 lg:pt-24 pb-14 lg:pb-20">
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] gap-8 lg:gap-14 items-start">
        {/* Left — copy */}
        <div>
          <p className="text-[10.5px] font-bold uppercase tracking-[0.22em] text-violet-700">
            Built for UK estate agencies
          </p>
          <h2 className="font-display text-[32px] sm:text-[40px] lg:text-[48px] font-bold tracking-[-0.028em] text-slate-900 leading-[1.04] mt-4">
            Fits into the way your agency<br />
            <span
              style={{
                background: "linear-gradient(90deg, #4F46E5 0%, #6D28D9 100%)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              already works.
            </span>
          </h2>
          <p className="text-[14px] sm:text-[15.5px] text-slate-600 leading-relaxed mt-5 max-w-[520px]">
            ChainFlow brings transaction progression into one dedicated workspace,
            giving your team visibility from sale agreed to exchange.
          </p>
        </div>

        {/* Right — factual product characteristics panel */}
        <div
          className="rounded-2xl bg-white ring-1 ring-slate-200/70 overflow-hidden"
          style={{ boxShadow: "0 24px 56px -30px rgba(15,23,42,0.14), 0 6px 14px -8px rgba(15,23,42,0.04)" }}
          data-testid="landing-agency-fit-panel"
        >
          <ul>
            {ROWS.map(({ Icon, label, body }, i) => (
              <li
                key={label}
                className={`px-5 lg:px-6 py-5 lg:py-6 flex items-start gap-4 ${i > 0 ? "border-t border-slate-100" : ""}`}
                data-testid={`landing-agency-fit-row-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <span
                  className="w-10 h-10 rounded-xl inline-flex items-center justify-center shrink-0 mt-0.5"
                  style={{ background: "#F5F3FF", color: "#6D28D9", border: "1px solid #E9D5FF" }}
                >
                  <Icon size={15} strokeWidth={2.2} />
                </span>
                <div className="min-w-0">
                  <p className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-slate-500">
                    {label}
                  </p>
                  <p className="text-[13.5px] text-slate-700 leading-relaxed mt-1.5">
                    {body}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  </section>
);

export default MarketingAgencyFit;
